import { createSlice, createAsyncThunk } from '@reduxjs/toolkit' 
import { STATUS } from 'features/enums'
import qaSessionService from './qaSessionService'

const errorMessage = (error) => {
  return (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
}

const initialState = { 
  sessions: [],
  closedSessions: [],
  statusFilter: "pending",
  sortQuestionsBy: "newest",
  myQuestions: [],
  sessionData: {
    isReady: false,
    title: "",
    questions: [],   
    requireApproval: false,
    questionVisibility: "",
    maxQuestions: 0,
    expiresAt: null,
  }, 

  pagination: {
    pending: { page: 1, hasMore: true, },
  },

  questions: {
    'pending_newest': { data: [], lastFetched: null  },
    'pending_oldest': { data: [], lastFetched: null  },
    'approved_newest': { data: [], lastFetched: null  },
    'approved_oldest': { data: [], lastFetched: null  },
    'approved_mostUpvoted': { data: [], lastFetched: null  },
    'answered_newest': { data: [], lastFetched: null  },
    'answered_oldest': { data: [], lastFetched: null }
  },
  pendingQuestions: [],
  approvedQuestions: [],
  answeredQuestions: [],
  createQASessionStatus: STATUS.NOT_STARTED, 
  getQASessionsStatus: STATUS.NOT_STARTED,
  getQASessionStatus: STATUS.NOT_STARTED,
  addQuestionStatus: STATUS.NOT_STARTED,
  endQASessionStatus: STATUS.NOT_STARTED,
  getQuestionsStatus: STATUS.NOT_STARTED,
  updateQuestionStatus: STATUS.NOT_STARTED,
  rejectedQuestionStatus: STATUS.NOT_STARTED,
  upvoteStatus: STATUS.NOT_STARTED,
  upvoteQuestionId: "",
  viewCreateQASessionModal: false,
  errorValue: ""
}

export const createQASession = createAsyncThunk(
  'qaSession/createQASession',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await qaSessionService.createQASession(data, token)
    } catch (error) {
    const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const endQASession = createAsyncThunk(
  'qaSession/endQASession',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await qaSessionService.endQASession(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)


export const getQASessions = createAsyncThunk(
  'qaSession/getQASessions',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await qaSessionService.getQASessions(data, token)
    } catch (error) {
    const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getQASession = createAsyncThunk(
  'qaSession/getQASession',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await qaSessionService.getQASession(data, token)
    } catch (error) {
    const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
) 

export const addQuestion = createAsyncThunk(
  'qaSession/addQuestion',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await qaSessionService.addQuestion(data, token)
    } catch (error) {
    const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const upvoteQuestion = createAsyncThunk(
  'qaSession/upvoteQuestion',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await qaSessionService.upvoteQuestion(data, token)
    } catch (error) {
    const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getQuestions = createAsyncThunk(
  'qaSession/getQuestions', 
  async ({ sessionId, status, churchId, sortValue },{ getState }) => {
    const cacheKey = `${status}_${sortValue}`;
    const token = getState().auth.user.token
    const state = getState().qASession; 
    const cached = state.questions[cacheKey];
    const now = Date.now();

    // Check cache (30 sec)
    if (cached?.lastFetched && now - cached.lastFetched < 30_000) {
      return { status, data: cached.data, fromCache: true };
    }

    // Otherwise, call API
    const res = await qaSessionService.getQuestions({ sessionId, status, churchId, sortValue }, token);
    return { cacheKey, data: res.questions, fromCache: false };
  } 
)

export const updateQuestionStatus = createAsyncThunk(
  'qaSession/updateQuestionStatus',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await qaSessionService.updateQuestionStatus(data, token)
    } catch (error) {
    const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export  const qaSessionSlice = createSlice({
    name: 'qASession',
    initialState,
    reducers: { 
      setViewCreateQASessionModal: (state, action) => {
        state.viewCreateQASessionModal = action.payload
      },
      resetUpvote: (state, action) => {
        state.upvoteStatus = STATUS.NOT_STARTED
        state.upvoteQuestionId =  ""
      },
      setStatusFilter: (state, action) => {
        state.statusFilter = action.payload
      },
      setQuestionSortValue: (state, action) => {
        state.sortQuestionsBy = action.payload
      },
      setUpvoteQuestionId: (state, action) => {
        state.upvoteQuestionId = action.payload
      },
      resetQuestions: (state) => {
        state.questions = {
          'answered_newest': { data: [], lastFetched: null  },
          'answered_oldest': { data: [], lastFetched: null  },
          'pending_newest': { data: [], lastFetched: null  },
          'pending_oldest': { data: [], lastFetched: null  },
          'approved_newest': { data: [], lastFetched: null  },
          'approved_oldest': { data: [], lastFetched: null  },
          'approved_mostUpvoted': { data: [], lastFetched: null }
        }
           
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(createQASession.pending, (state) => { 
          state.createQASessionStatus = STATUS.LOADING
        })
        .addCase(createQASession.fulfilled, (state, action) => { 
          state.createQASessionStatus = STATUS.SUCCESS
          // state.sessions.push(action.payload.session)
          state.viewCreateQASessionModal = false
        })
        .addCase(createQASession.rejected, (state, action) => { 
          state.createQASessionStatus = STATUS.ERROR
        })
        //%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
        .addCase(getQASessions.pending, (state) => { 
          state.getQASessionsStatus = STATUS.LOADING
        })
        .addCase(getQASessions.fulfilled, (state, action) => { 
          state.getQASessionsStatus = STATUS.SUCCESS 
          state.sessions = action.payload.sessions
        })
        .addCase(getQASessions.rejected, (state, action) => { 
          state.getQASessionsStatus = STATUS.ERROR
        })
        //%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
        .addCase(getQASession.pending, (state) => { 
          state.getQASessionStatus = STATUS.LOADING
        })
        .addCase(getQASession.fulfilled, (state, action) => { 
          state.getQASessionStatus = STATUS.SUCCESS 
          state.myQuestions = action.payload.myQuestions
          state.sessionData = {
            ...state.sessionData,
            isReady: true,
            questionVisibility: action.payload.session.questionVisibility,
            title: action.payload.session.title, 
            church: action.payload.session.church,
            status: action.payload.session.status,
            requireApproval: action.payload.session.requireApproval,
            allowAnonymous: action.payload.session.allowAnonymous, 
            maxQuestions: action.payload.session.maxQuestionsPerUser,
            expiresAt: action.payload.session.expiresAt
          }
        })
        .addCase(getQASession.rejected, (state, action) => { 
          state.getQASessionStatus = STATUS.ERROR
        })
        //%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
        .addCase(addQuestion.pending, (state) => { 
          state.addQuestionStatus = STATUS.LOADING
          
        })
        .addCase(addQuestion.fulfilled, (state, action) => { 
          state.addQuestionStatus = STATUS.SUCCESS 
          state.myQuestions.push(action.payload.question)
        })
        .addCase(addQuestion.rejected, (state, action) => { 
          state.addQuestionStatus = STATUS.ERROR
          state.errorValue = action.payload
        })
        //%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
        .addCase(getQuestions.pending, (state) => { 
          state.getQuestionsStatus = STATUS.LOADING
        })
        .addCase(getQuestions.fulfilled, (state, action) => { 
          
          const { cacheKey, data, fromCache } = action.payload; 

          // If data came from cache, don't overwrite timestamp
          if (!fromCache) {
            state.questions[cacheKey] = {
              data: data,
              lastFetched: Date.now(),
            };
          }

          state.getQuestionsStatus = STATUS.SUCCESS 
         })
        .addCase(getQuestions.rejected, (state, action) => { 
          state.getQuestionsStatus = STATUS.ERROR
        })
        //%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
        .addCase(upvoteQuestion.pending, (state) => { 
          state.upvoteStatus = STATUS.LOADING
        })
        .addCase(upvoteQuestion.fulfilled, (state, action) => { 
          
          state.upvoteStatus = STATUS.SUCCESS  
          
         })
        .addCase(upvoteQuestion.rejected, (state, action) => { 
          state.upvoteStatus = STATUS.ERROR
          state.errorValue = action.payload
        })
        //%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
        .addCase(updateQuestionStatus.pending, (state) => { 
          state.updateQuestionStatus = STATUS.LOADING
        })
        .addCase(updateQuestionStatus.fulfilled, (state, action) => { 
          state.updateQuestionStatus = STATUS.SUCCESS  
          state.statusFilter = action.payload.statusFilter
          // state.questions[`action.payload.statusFilter`].data = state.questions[action.payload.statusFilter].data.filter((question => question._id !== action.payload.questionId))
          state.questions[`${action.payload.previousStatus}_${state.sortQuestionsBy}`].data = state.questions[`${action.payload.previousStatus}_${state.sortQuestionsBy}`].data.filter((question => question._id !== action.payload.question._id))
          // state.questions[action.payload.newStatus].data.push(action.payload.question)
          state.questions[`${action.payload.newStatus}_${state.sortQuestionsBy}`].data.push(action.payload.question)
        
        })
        .addCase(updateQuestionStatus.rejected, (state, action) => { 
          state.updateQuestionStatus = STATUS.ERROR
          state.errorValue = action.payload
        })
        //%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
        .addCase(endQASession.pending, (state) => { 
          state.endQASessionStatus = STATUS.LOADING
        })
        .addCase(endQASession.fulfilled, (state, action) => { 
          state.endQASessionStatus = STATUS.SUCCESS   

         })
        .addCase(endQASession.rejected, (state, action) => { 
          state.endQASessionStatus = STATUS.ERROR
          state.errorValue = action.payload
        })
    },
})


export const { setQuestionSortValue, resetQuestions, setUpvoteQuestionId, setStatusFilter, setViewCreateQASessionModal, resetUpvote  } = qaSessionSlice.actions
export default qaSessionSlice.reducer
